import { AppStore } from "@/store/AppStore";
import { GatedFeature } from "@/store/feature-gates/types";
import {
  PaywallContext,
  type FeatureGateDeniedEventParams,
  type FeatureGateEventParams,
  type FeatureGateLevel,
  type PaywallEventParams,
  type ProactivePaywallSource,
} from "@/domains/metrics/tracked-event";
import { SubscriptionPlanKind, SubscriptionPlanVariant } from "@/store/subscriptions/types";
import type { CommercialPlanKey } from "@/modules/commercial-plans";
import { getCompatibleCommercialCatalog } from "@/modules/commercial-plans";

export interface MonetizationUsagePayload {
  note_create_quota_used: number;
  note_create_quota_limit: number | typeof Infinity;
  note_create_usage_ratio: number;
  chat_message_send_quota_used: number;
  chat_message_send_quota_limit: number | typeof Infinity;
  chat_message_send_chat_usage_ratio: number;
  native_chat_message_send_quota_used: number;
  native_chat_message_send_quota_limit: number | typeof Infinity;
  native_chat_message_send_usage_ratio: number;
  content_understanding_pdf_page_quota_used: number;
  content_understanding_pdf_page_quota_limit: number | typeof Infinity;
  content_understanding_pdf_page_usage_ratio: number;
}

type GateCheckResult = ReturnType<AppStore["featureGates"]["checkGate"]>;

export function buildMonetizationUsagePayload(store: AppStore): MonetizationUsagePayload {
  const noteGate = store.featureGates.checkGate(GatedFeature.NoteCreate);
  const chatGate = store.featureGates.checkGate(GatedFeature.ChatMessageSend);
  const nativeChatGate = store.featureGates.checkGate(GatedFeature.NativeChatMessageSend);
  const pdfGate = store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage);

  const safeRatio = (used: number, limit: number | typeof Infinity) =>
    limit === Infinity || limit === 0 ? 0 : used / (limit as number);

  return {
    note_create_quota_used: noteGate.used,
    note_create_quota_limit: noteGate.limit,
    note_create_usage_ratio: safeRatio(noteGate.used, noteGate.limit),
    chat_message_send_quota_used: chatGate.used,
    chat_message_send_quota_limit: chatGate.limit,
    chat_message_send_chat_usage_ratio: safeRatio(chatGate.used, chatGate.limit),
    native_chat_message_send_quota_used: nativeChatGate.used,
    native_chat_message_send_quota_limit: nativeChatGate.limit,
    native_chat_message_send_usage_ratio: safeRatio(nativeChatGate.used, nativeChatGate.limit),
    content_understanding_pdf_page_quota_used: pdfGate.used,
    content_understanding_pdf_page_quota_limit: pdfGate.limit,
    content_understanding_pdf_page_usage_ratio: safeRatio(pdfGate.used, pdfGate.limit),
  };
}

const toFeatureGateEventPayload = ({
  feature,
  gate,
  level,
}: {
  feature: GatedFeature;
  gate: GateCheckResult;
  level: FeatureGateLevel;
}): FeatureGateEventParams => {
  return {
    feature,
    level,
    used: gate.used,
    limit: gate.limit,
    warn_at: gate.warnAt,
  };
};

/**
 * Picks the feature to route the banner click to. When multiple limits are
 * exceeded at once, `modalFeature` is undefined so the upsell modal renders
 * the generic "monthly usage limits" copy instead of the per-feature copy.
 * `eventFeature` is always a concrete feature for analytics continuity.
 */
export const resolveActiveBannerFeatureGate = ({
  store,
}: {
  store: AppStore;
}): {
  modalFeature: GatedFeature | undefined;
  eventFeature: GatedFeature;
  gate: GateCheckResult;
} => {
  const noteGate = store.featureGates.checkGate(GatedFeature.NoteCreate);
  const chatGate = store.featureGates.checkGate(GatedFeature.ChatMessageSend);
  const nativeChatGate = store.featureGates.checkGate(GatedFeature.NativeChatMessageSend);
  const pdfGate = store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage);

  const exceeded: { feature: GatedFeature; gate: GateCheckResult }[] = [];
  if (!noteGate.allowed) exceeded.push({ feature: GatedFeature.NoteCreate, gate: noteGate });
  if (!chatGate.allowed) exceeded.push({ feature: GatedFeature.ChatMessageSend, gate: chatGate });
  if (!nativeChatGate.allowed) {
    exceeded.push({ feature: GatedFeature.NativeChatMessageSend, gate: nativeChatGate });
  }
  if (!pdfGate.allowed) {
    exceeded.push({ feature: GatedFeature.ContentUnderstandingPdfPage, gate: pdfGate });
  }

  if (exceeded.length > 1) {
    return { modalFeature: undefined, eventFeature: exceeded[0].feature, gate: exceeded[0].gate };
  }

  if (exceeded.length === 1) {
    const only = exceeded[0];
    return { modalFeature: only.feature, eventFeature: only.feature, gate: only.gate };
  }

  const isAtWarn = (g: GateCheckResult) => g.warnAt !== null && g.used >= g.warnAt;

  if (isAtWarn(noteGate)) {
    return {
      modalFeature: GatedFeature.NoteCreate,
      eventFeature: GatedFeature.NoteCreate,
      gate: noteGate,
    };
  }

  if (isAtWarn(chatGate)) {
    return {
      modalFeature: GatedFeature.ChatMessageSend,
      eventFeature: GatedFeature.ChatMessageSend,
      gate: chatGate,
    };
  }

  if (isAtWarn(nativeChatGate)) {
    return {
      modalFeature: GatedFeature.NativeChatMessageSend,
      eventFeature: GatedFeature.NativeChatMessageSend,
      gate: nativeChatGate,
    };
  }

  if (isAtWarn(pdfGate)) {
    return {
      modalFeature: GatedFeature.ContentUnderstandingPdfPage,
      eventFeature: GatedFeature.ContentUnderstandingPdfPage,
      gate: pdfGate,
    };
  }

  return {
    modalFeature: GatedFeature.NoteCreate,
    eventFeature: GatedFeature.NoteCreate,
    gate: noteGate,
  };
};

export function buildFeatureGateBannerEventPayload({
  store,
  level,
}: {
  store: AppStore;
  level: FeatureGateLevel;
}): FeatureGateEventParams {
  const { eventFeature, gate } = resolveActiveBannerFeatureGate({ store });
  return toFeatureGateEventPayload({ feature: eventFeature, gate, level });
}

export function buildPaywallEventPayload({
  store,
  context,
  feature,
  level,
  targetCommercialPlanKey,
  commercialUpgradeAction,
}: {
  store: AppStore;
  context?: string;
  feature?: GatedFeature;
  level?: FeatureGateLevel;
  targetCommercialPlanKey?: CommercialPlanKey;
  commercialUpgradeAction?: "review_plans" | "select_plan";
}): PaywallEventParams {
  const monetizationUsagePayload = buildMonetizationUsagePayload(store);
  const commercialPlanSnapshot = store.platformInfo?.commercialPlanSnapshot;
  const commercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo?.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });
  const commercialPlanPayload = {
    ...(commercialCatalog && commercialPlanSnapshot
      ? {
          commercial_catalog_generation: commercialCatalog.generation,
          current_commercial_plan_key: commercialPlanSnapshot.currentPlan.key,
        }
      : {}),
    ...(targetCommercialPlanKey
      ? {
          commercial_plan_key: targetCommercialPlanKey,
          target_commercial_plan_key: targetCommercialPlanKey,
        }
      : {}),
    ...(commercialUpgradeAction ? { commercial_upgrade_action: commercialUpgradeAction } : {}),
  };
  const shouldIncludeFeatureGatePayload = Boolean(
    feature &&
    level &&
    feature !== GatedFeature.DarkMode &&
    context !== PaywallContext.ChatModelSelection &&
    context !== PaywallContext.DarkModeToggle
  );

  if (!shouldIncludeFeatureGatePayload || !feature || !level) {
    return {
      context,
      ...monetizationUsagePayload,
      ...commercialPlanPayload,
    };
  }

  return {
    context,
    ...monetizationUsagePayload,
    ...commercialPlanPayload,
    ...toFeatureGateEventPayload({
      feature,
      gate: store.featureGates.checkGate(feature),
      level,
    }),
  };
}

export function buildSubscriptionPlanMetricsPayload({
  store,
  targetPlanKind,
  targetPlanVariant,
}: {
  store: AppStore;
  targetPlanKind: SubscriptionPlanKind;
  targetPlanVariant?: SubscriptionPlanVariant;
}): Pick<
  PaywallEventParams,
  "current_plan_kind" | "effective_plan_kind" | "target_plan_kind" | "target_plan_variant"
> {
  const { currentPlanKind, effectivePlanKind } = resolveSubscriptionPlanKinds({ store });

  return {
    current_plan_kind: currentPlanKind,
    effective_plan_kind: effectivePlanKind,
    target_plan_kind: targetPlanKind,
    ...(targetPlanVariant ? { target_plan_variant: targetPlanVariant } : {}),
  };
}

export function buildOriginalSubscriptionPlanMetricsPayload({
  store,
  originalPlanKind,
  originalEffectivePlanKind,
}: {
  store: AppStore;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
}): {
  original_plan_kind: SubscriptionPlanKind;
  original_effective_plan_kind: SubscriptionPlanKind;
} {
  const { currentPlanKind, effectivePlanKind } = resolveSubscriptionPlanKinds({ store });
  const resolvedOriginalPlanKind = originalPlanKind ?? currentPlanKind;

  return {
    original_plan_kind: resolvedOriginalPlanKind,
    original_effective_plan_kind:
      originalEffectivePlanKind ?? effectivePlanKind ?? resolvedOriginalPlanKind,
  };
}

export function buildSubscriptionPlanKindMetricsPayload({ store }: { store: AppStore }): {
  subscription_plan_kind: SubscriptionPlanKind;
} {
  const { currentPlanKind } = resolveSubscriptionPlanKinds({ store });

  return {
    subscription_plan_kind: currentPlanKind,
  };
}

function resolveSubscriptionPlanKinds({ store }: { store: AppStore }): {
  currentPlanKind: SubscriptionPlanKind;
  effectivePlanKind: SubscriptionPlanKind;
} {
  const currentPlanKind =
    store.subscriptions.billingDisplayPlanKind ??
    store.subscriptions.featureAccess?.planKind ??
    SubscriptionPlanKind.UNKNOWN;
  const effectivePlanKind = store.subscriptions.effectiveDisplayPlanKind ?? currentPlanKind;

  return {
    currentPlanKind,
    effectivePlanKind,
  };
}

export function buildProactivePaywallEventPayload({
  store,
  originalPlanKind,
  originalEffectivePlanKind,
  source: sourceOverride,
}: {
  store: AppStore;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
  source?: ProactivePaywallSource;
}): PaywallEventParams {
  const source = sourceOverride ?? store.settingsModal?.memAgentSettingsEntrySource ?? undefined;
  const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;

  return {
    ...buildPaywallEventPayload({
      store,
      context: PaywallContext.Proactive,
    }),
    ...(source ? { source } : {}),
    ...(proactivePricingAssignment
      ? {
          experiment_key: proactivePricingAssignment.experiment_key,
          experiment_variant_key: proactivePricingAssignment.variant_key,
        }
      : {}),
    ...buildSubscriptionPlanMetricsPayload({
      store,
      targetPlanKind: SubscriptionPlanKind.PROACTIVE,
    }),
    ...buildOriginalSubscriptionPlanMetricsPayload({
      store,
      originalPlanKind,
      originalEffectivePlanKind,
    }),
  };
}

export function buildFeatureGateDeniedEventPayload({
  feature,
  context,
  gate,
}: {
  feature: GatedFeature;
  context?: string;
  gate: GateCheckResult;
}): FeatureGateDeniedEventParams {
  return {
    context,
    ...toFeatureGateEventPayload({
      feature,
      gate,
      level: "exceeded",
    }),
  };
}
